import * as Phaser from "phaser";
import { Scene, Tilemaps, GameObjects, Physics, Math as Mathph } from "phaser";
import { Player } from "../../classes/player";
import { NPC } from "../../classes/npc";
import { DIRECTION } from "../../utils";
import UIPlugin from "../../../phaser3-rex-plugins/templates/ui/ui-plugin";
import BoardPlugin from "../../../phaser3-rex-plugins/plugins/board-plugin";
import { PathFinder } from "../../../phaser3-rex-plugins/plugins/board-components";
import { TileXYType } from "../../../phaser3-rex-plugins/plugins/board/types/Position";
import { shuffle } from "../../utils";
import { startMission } from "./startMission";

export class DynamicScene extends Scene {
  public mission: any;
  public manifest: any;
  public startMission: any = startMission;

  protected timeFrame: number = 0;
  protected isQuerying: boolean = false;

  protected map: Tilemaps.Tilemap;
  protected tileset: Tilemaps.Tileset;
  protected groundLayer: Tilemaps.TilemapLayer;
  protected wallLayer: Tilemaps.TilemapLayer;
  protected flowerLayer: Tilemaps.TilemapLayer;
  protected treeLayer: Tilemaps.TilemapLayer;
  protected aboveLayer: Tilemaps.TilemapLayer;

  protected player: Player;
  protected npcGroup: GameObjects.Group;
  protected keySpace: Phaser.Input.Keyboard.Key;
  protected keyEnter: Phaser.Input.Keyboard.Key;
  public rexUI: UIPlugin;
  public rexBoard: BoardPlugin;
  protected board: BoardPlugin.Board;
  protected pathFinder: PathFinder;

  constructor() {
    super("DynamicScene");

    let missionData = JSON.parse(localStorage.getItem("selectedMission"));

    if (!missionData || typeof missionData !== "object") {
      missionData = this.startMission;
      console.log("no mission data found, using default mission");
    }

    this.mission = missionData;
  }

  init(): void {
    this.manifest = this.convertToJSON(this.mission);
    this.cache.json.add("manifest", this.manifest);
  }

  preload(): void {
    this.load.baseURL = process.env.PUBLIC_URL + "assets/town/";

    this.load.spritesheet("User", "npcs/main6.png", {
      frameWidth: 15,
      frameHeight: 23,
    });

    this.load.spritesheet("Innocent Bystander 1", "npcs/npc3.png", {
      frameWidth: 14,
      frameHeight: 20,
    });

    this.load.spritesheet("Innocent Bystander 2", "npcs/npc6.png", {
      frameWidth: 16,
      frameHeight: 20,
    });

    this.load.spritesheet("Innocent Bystander 3", "npcs/npc1.png", {
      frameWidth: 16,
      frameHeight: 21,
    });

    this.load.spritesheet("Innocent Bystander 4", "npcs/npc4.png", {
      frameWidth: 16,
      frameHeight: 20,
    });

    this.load.spritesheet("Innocent Bystander 5", "npcs/npc5.png", {
      frameWidth: 16,
      frameHeight: 20,
    });

    this.load.spritesheet("Innocent Bystander 6", "npcs/npc2.png", {
      frameWidth: 14,
      frameHeight: 21,
    });

    this.load.image({
      key: "tiles",
      url: "tilemaps/tiles/htmaa-tileset.png",
    });
    this.load.tilemapTiledJSON("town", "tilemaps/json/htmaa-lab.json");

    this.loadAssets();
    this.loadCharacterAssets();
  }

  loadAssets(): void {
  }

  initMap(): void {
    this.map = this.make.tilemap({
      key: "town",
      tileWidth: 16,
      tileHeight: 16,
    });
    this.tileset = this.map.addTilesetImage("town", "tiles")!;
    this.groundLayer = this.map.createLayer("ground", this.tileset, 0, 0)!;
    this.wallLayer = this.map.createLayer("wall", this.tileset, 0, 0)!;
    this.flowerLayer = this.map.createLayer("flower", this.tileset, 0, 0)!;
    this.treeLayer = this.map.createLayer("tree", this.tileset, 0, 0)!;
    this.aboveLayer = this.map.createLayer("above", this.tileset, 0, 0)!;

    this.aboveLayer.setDepth(Number.MAX_SAFE_INTEGER);

    this.wallLayer.setCollisionByProperty({ collides: true });
    this.treeLayer.setCollisionByProperty({ collides: true });
    this.board = this.rexBoard.createBoardFromTilemap(this.map);
    this.board.getAllChess().forEach((chess: any) => {
      var collide = ["flower", "wall", "tree"].includes(chess.layer.name);
      if (collide && chess.index != -1) {
        chess.rexChess.setBlocker();
      }
    });
    this.pathFinder = this.rexBoard.add.pathFinder({
      occupiedTest: true,
      blockerTest: true,
      pathMode: "straight",
      cacheCost: true,
    });
  }

  initSprite(): void {
    this.npcGroup = this.add.group();
    var npcPoints = this.map.filterObjects("npcs", (npc) => {
      return npc.type === "npc";
    });
    for (let i = 0; i < npcPoints.length; i++) {
      var npcPoint = this.map.findObject("npcs", (npc) => {
        // @ts-ignore
        for (let j = 0; j < npc.properties.length; j++) {
          // @ts-ignore
          if (npc.properties[j].name === "id") {
            // @ts-ignore
            return npc.properties[j].value === i;
          }
        }
      });
      var tileXY = this.board.worldXYToTileXY(npcPoint.x, npcPoint.y);
      var npc = new NPC(
        this,
        this.board,
        npcPoint.x,
        npcPoint.y,
        npcPoint.name,
        npcPoint.properties[0].value
      );
      npc.setScale(1.35);
      this.board.addChess(npc, tileXY.x, tileXY.y, 0, true);
      this.physics.add.collider(npc, this.npcGroup);
      this.npcGroup.add(npc);
    }

    this.physics.add.collider(this.npcGroup, this.wallLayer);
    this.physics.add.collider(this.npcGroup, this.treeLayer);
    this.physics.add.collider(this.npcGroup, this.npcGroup); //remove for NPCs to collide

    const locations = [
      [321, 333],
      [569, 299],
      [406, 167],
      [442, 343],
      [279, 338]
    ];

    const randomIndex = Math.floor(Math.random() * locations.length);
    const selectedLocation = locations[randomIndex];

    this.player = new Player(this, selectedLocation[0], selectedLocation[1]);
    this.player.setScale(1.35);

    this.physics.add.collider(this.player, this.wallLayer);
    this.physics.add.collider(this.player, this.treeLayer);
    // this.physics.add.collider(this.player, this.houseLayer);
    this.physics.add.collider(
      this.player,
      this.npcGroup,
      (player: Player, npc: NPC) => {
        npc.pauseMoving();
        var checkResumeWalk = this.time.addEvent({
          delay: 1000,
          callback: () => {
            const nearbyDistance = 1.1 * Math.max(player.width, player.height);
            var distance = Mathph.Distance.Between(
              player.x,
              player.y,
              npc.x,
              npc.y
            );
            if (distance > nearbyDistance) {
              npc.resumeMoving();
              checkResumeWalk.destroy();
            }
          },
        });
      }
    );

    this.keySpace.on("up", () => {
      var ret = getNearbyNPC(this.player, this.npcGroup);
      var npc = ret[0];
      if (npc) {
        npc = npc as NPC;
        (npc as NPC).changeDirection(ret[1]);
        (npc as NPC).setTalking(true);
        // this.createInputBox(npc);
        if (typeof window.ReactInterface.openPrompt === "function") {
          window.ReactInterface.openPrompt(npc);
        }
      }
    });
    // this.keyEnter.on("up", () => {});

    this.physics.world.setBounds(
      0,
      0,
      this.groundLayer.width + this.player.width,
      this.groundLayer.height
    );
  }

  convertToJSON(missionDict) {
    // console.log("CONVERTING MISSION THIS TO JSON: ", missionDict);
    if (
      !missionDict ||
      !missionDict.Buildings ||
      !Array.isArray(missionDict.Buildings)
    ) {
      console.error(
        `Missing or invalid  Buildings information in input dictionary:  ${JSON.stringify(
          missionDict
        )}`
      );
      return null;
    }

    const output = {
      assets: missionDict.Buildings.map((building, index) => {
        if (!building.BuildingURL) {
          return null;
        }

        // return {
        //   type: "image",
        //   key: `GeneratedLocation${index + 1}`,
        //   path: building.BuildingURL,
        //   xPos: 240 + 176 * index,
        //   yPos: 144,
        // };

        return {
          type: "image",
          key: building.Name,
          path: building.BuildingURL,
          xPos: 240 + 176 * index,
          yPos: 144,
        };
      }).filter(Boolean),
    };

    // console.log("OUTPUT FROM CONVERTTOJSON: ", output);
    return output;
  }

  loadCharacterAssets(): void {
    this.mission.Buildings.forEach((building, i) => {
      building.Characters.forEach((character, j) => {
        this.load.image(
          `GeneratedLocation${i + 1}Character${j + 1}`,
          character.SpriteAssetURL
        );
      });
    });
  }

  createCharacterSprites(): void {
    this.mission.Buildings.forEach((building, i) => {
      building.Characters.forEach((character, j) => {
        const randomLocation = this.getRandomTileAtLocation(
          `GeneratedLocation${i + 1}`
        );

        const characterSprite = this.physics.add
          .image(
            randomLocation.x * 16,
            randomLocation.y * 16,
            `GeneratedLocation${i + 1}Character${j + 1}`
          )
          .setDepth(10)
          .setScale(1.35);

        characterSprite.setImmovable(true);
        this.physics.add.collider(this.player, characterSprite);

        const buffer = 11;
        characterSprite.body.setSize(
          characterSprite.width + buffer,
          characterSprite.height + buffer
        );

        const overlapArea = this.add.zone(
          characterSprite.x,
          characterSprite.y,
          characterSprite.width + 2 * buffer,
          characterSprite.height + 2 * buffer
        );

        this.physics.world.enable(
          overlapArea,
          Phaser.Physics.Arcade.STATIC_BODY
        );
        overlapArea.setSize(
          characterSprite.width + 2 * buffer,
          characterSprite.height + 2 * buffer
        );
        overlapArea.setVisible(false);

        this.physics.add.overlap(
          this.player,
          overlapArea,
          () => this.onPlayerOverlap(character),
          null,
          this
        );
      });
    });
  }

  onPlayerOverlap(character): void {
    // console.log(character);

    if (typeof window.ReactInterface.showCharacterChat === "function") {
      // we're passing the character object to React Interface
      window.ReactInterface.showCharacterChat(character);
    }
  }

  createGameObjects(): void {
    // const manifest = this.cache.json.get("manifest");

    // if (manifest && manifest.assets) {
    //   manifest.assets.forEach((asset) => {
    //     if (asset.type === "image") {
    //       const building = this.add.image(asset.xPos, asset.yPos, asset.key);
    //       building.setDepth(9);

    //     }
    //   });
    }

  create(): void {
    const manifest = this.cache.json.get("manifest");
    // console.log("DYNAMICSCENE CREATE(DATA), data: ", this.mission, this.manifest, manifest);

    window.gameInstance = this;
    this.keySpace = this.input.keyboard!.addKey("SPACE");
    this.keyEnter = this.input.keyboard!.addKey("ENTER");
    this.initMap();
    this.initSprite();
    this.initCamera();
    this.cameras.main.setZoom(2.7);

    window.gameInstance.submitPrompt = this.submitPrompt.bind(this);

    if (!manifest) {
      console.error(
        `Failed to convert mission ${this.mission} to asset manifest`
      );
      return;
    }

    // manifest.assets.forEach((asset) => {
    //   if (asset.type === "image") {
    //     this.load.image(asset.key, asset.path);
    //   }
    // });

    this.load.once(Phaser.Loader.Events.COMPLETE, () => {
      this.createGameObjects();
      this.createCharacterSprites();
    });

    this.load.start();
  }

  update(time, delta): void {
    this.timeFrame += delta;
    this.player.update();

    this.npcGroup.getChildren().forEach(function (npc) {
      (npc as NPC).update();
    });
    if (this.timeFrame > 5000) {
      if (!this.isQuerying) {
        this.isQuerying = true;
        var allNpcs = this.npcGroup.getChildren();
        var shouldUpdate = [];

        for (let i = 0; i < this.npcGroup.getLength(); i++) {
          if (
            !(allNpcs[i] as NPC).isMoving() &&
            !(allNpcs[i] as NPC).isTalking()
          ) {
            shouldUpdate.push(i);
          }
        }

        fetch("https://htmaaland-3c97b27faa21.herokuapp.com/make_decision", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "same-origin",
          body: JSON.stringify({
            agent_ids: shouldUpdate,
          }),
        }).then((response) => {
          try {
            response.json().then((data) => {
              this.npcGroup.getChildren().forEach(function (npc) {
                (npc as NPC).destroyTextBox();
              });
              for (let i = 0; i < data.length; i++) {
                let npc = allNpcs[shouldUpdate[i]] as NPC;
                let emoji = data[i].EmojiThoughts;
                let action = data[i].Action;
                let actionInput = data[i].ActionInput;

                let tile;

                switch (action) {
                  case "MoveTo":
                    tile = this.getRandomTileAtLocation(actionInput.to);
                    if (tile == undefined) break;
                    // npc.destroyTextBox();
                    this.moveNPC(
                      shouldUpdate[i],
                      tile,
                      undefined,
                      actionInput.to
                    );
                    break;

                  // doesn't get called to save costs
                  case "Speak":
                    let ret = this.getNPCNeighbor(actionInput.to);
                    tile = ret[0];
                    let finalDirection = ret[1];
                    let listener = ret[2];
                    if (tile == undefined) break;
                    this.moveNPC(
                      shouldUpdate[i],
                      tile,
                      finalDirection,
                      undefined,
                      listener
                    );
                    npc.setTextBox(actionInput.text);
                    break;

                  default:
                    npc.setTextBox(emoji);
                    break;
                }
              }
              this.isQuerying = false;
            });
          } catch (error) {
            console.error("Error: ", error);
          }
        });
      }
      this.timeFrame = 0;
    }
  }

  initCamera(): void {
    this.cameras.main.setSize(this.game.scale.width, this.game.scale.height);
    this.cameras.main.setBounds(
      0,
      0,
      this.groundLayer.width,
      this.groundLayer.height
    );
    this.cameras.main.startFollow(this.player, true, 0.09, 0.09);
    this.cameras.main.setZoom(4);
  }

  getLocation(buildingName) {
    console.log("buildingName", buildingName);
    const buildingIndex = this.mission.Buildings.findIndex(building => building.Name === buildingName);
    console.log("buildingIndex", buildingIndex);
    if (buildingIndex === -1) return -1;
    return `GeneratedLocation${buildingIndex + 1}`;
  }

  submitPrompt(prompt: string, npc: Physics.Arcade.Sprite) {
    fetch("https://htmaaland-3c97b27faa21.herokuapp.com/chat", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "same-origin",
      body: JSON.stringify({
        content: prompt,
        history: "",
        mission: "",
        name: "Helpful bystander",
        role: "a fellow student working on projects for MIT Media Lab's class, How to Make (almost) Anything",
      }),
    })
      .then((response) => {
        response.json().then((data) => {
          console.log("DATA RECEIVED!", data);

          if (data.Action === "MoveTo") {
            let actionInput = data.ActionInput;
            (npc as NPC).setTalking(false);
            let location = this.getLocation(actionInput.Moveto);
            if (location == -1) {
              window.gameInstance.events.emit(
                "update-text",
                `404: ${actionInput.Moveto} doesn't exist on this map`
              );
              return;
            }
            let moveToTile = this.getRandomTileAtLocation(String(location));
            this.moveNPC(npc.id, moveToTile);
            window.gameInstance.events.emit(
              "update-text",
              `[Innocent bystander is going to ${actionInput.Moveto}]`
            );
          } else if (data.Action === "Speak"){
            let text = data.ActionInput.text;
            console.log("RECEIVED: ", text, npc);

            window.gameInstance.events.emit("update-text", text);
            console.log(" ");
            console.log("############TOWN.ts############");
            console.log("update-text emitted: ", text);
            (npc as NPC).setTalking(false);
          }
        });
      })
      .catch((error) => {
        console.error("Chat error: ", error);
      });
  }

  getRandomTileAtLocation(location_name: string): TileXYType {
    // console.log("All location names: ");
    // this.map.objects
    //   .filter((layer) => layer.name == "location")[0]
    //   .objects.map((object) => console.log(object.name));
    var location = this.map.findObject("location", function (object) {
      return object.name == location_name;
    });
    var x = location.x;
    var y = location.y;
    var width = location.width;
    var height = location.height;
    var cnt = 0;
    // debugger;
    do {
      if (cnt > 10) {
        console.log("Failed to find a random tile");
        return null;
      }
      var worldX = Math.floor(Math.random() * width) + x;
      var worldY = Math.floor(Math.random() * height) + y;
      var tile = this.board.worldXYToTileXY(worldX, worldY);
      cnt++;
    } while (
      this.board.hasBlocker(tile.x, tile.y) || // has wall
      this.board.tileXYToChessArray(tile.x, tile.y).length !=
        this.map.layers.length // has npc
    );
    return tile;
  }

  getNPCNeighbor(npc_name: string): [TileXYType, number, NPC] {
    // @ts-ignore
    var npc = this.npcGroup.getChildren().find((npc) => {
      return (npc as NPC).name == npc_name;
    }) as NPC;

    if (!npc) {
      console.error(`NPC with name ${npc_name} not found.`);
      return [{ x: 0, y: 0 }, DIRECTION.DOWN, undefined];
    }

    var npcTile = this.board.worldXYToTileXY(npc.x, npc.y);
    var directions = [
      [-1, 0],
      [1, 0],
      [0, -1],
      [0, 1],
    ];
    var order = shuffle([0, 1, 2, 3]);
    var tileX = undefined;
    var tileY = undefined;
    var finalDirection = -1; // Initiate direction with a default invalid value

    for (let i = 0; i < 4; i++) {
      var direction = directions[order[i]];
      var tmpX = npcTile.x + direction[0];
      var tmpY = npcTile.y + direction[1];

      if (
        !this.board.hasBlocker(tmpX, tmpY) && // no wall
        this.board.tileXYToChessArray(tmpX, tmpY).length ==
          this.map.layers.length // no npc
      ) {
        tileX = tmpX;
        tileY = tmpY;

        if (direction && direction[0] == 0 && direction[1] == 1) {
          finalDirection = DIRECTION.UP;
        } else if (direction && direction[0] == 0 && direction[1] == -1) {
          finalDirection = DIRECTION.DOWN;
        } else if (direction && direction[0] == 1 && direction[1] == 0) {
          finalDirection = DIRECTION.LEFT;
        } else if (direction && direction[0] == -1 && direction[1] == 0) {
          finalDirection = DIRECTION.RIGHT;
        }

        break;
      }
    }

    if (tileX === undefined || tileY === undefined) {
      console.warn("Could not find a suitable tile for the NPC");
      return [npcTile, DIRECTION.DOWN, npc]; // Return NPC's current tile and direction
    }

    return [{ x: tileX, y: tileY }, finalDirection, npc];
  }

  moveNPC(
    npcId: number,
    tile,
    finalDirection: number = undefined,
    targetLocation: string = undefined,
    targetNPC: NPC = undefined
  ): void {
    var npc = this.npcGroup.getChildren()[npcId] as NPC;
    var npc_chess = this.board.worldXYToChess(npc.x, npc.y);
    this.pathFinder.setChess(npc_chess);
    // var tmp = this.board.chessToTileXYZ(npc_chess);
    var path = this.pathFinder.findPath({
      x: tile.x,
      y: tile.y,
    } as TileXYType);
    npc.setTargetNPC(targetNPC);
    npc.moveAlongPath(path, finalDirection, targetLocation);
  }
}

function getNearbyNPC(
  player: Physics.Arcade.Sprite,
  npcGroup: GameObjects.Group
): [Physics.Arcade.Sprite | null, number] {
  var nearbyObject: Physics.Arcade.Sprite | null = null;

  // Not rigorous. Just a rough estimation. Requires that the npcs have
  // similar width and height to player.
  const nearbyDistance = 1.1 * Math.max(player.width, player.height);

  var direction = 0;
  npcGroup.getChildren().forEach(function (object) {
    var _object = object as Physics.Arcade.Sprite;
    const distance = Mathph.Distance.Between(
      player.x,
      player.y,
      _object.x,
      _object.y
    );

    if (distance <= nearbyDistance) {
      nearbyObject = _object;
      var x_ratio = (player.x - _object.x) / _object.width;
      var y_ratio = (player.y - _object.y) / _object.height;
      if (Math.abs(x_ratio) > Math.abs(y_ratio)) {
        if (x_ratio > 0) {
          direction = DIRECTION.RIGHT;
        } else {
          direction = DIRECTION.LEFT;
        }
      } else {
        if (y_ratio > 0) {
          direction = DIRECTION.DOWN;
        } else {
          direction = DIRECTION.UP;
        }
      }
    }
  });

  return [nearbyObject, direction];
}
